import { SignatureInput, TextInput, ConfirmModal, CustomButton } from '@/shared/ui/components';
import { getContent } from '@/entities/study';
import { inforBlackIcon, inforOrangeIcon } from '@/shared/ui/assets';
import { auth } from '@/shared/lib';
import { Drawer } from 'antd';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { useEditPhysicianAmendmentNotesDrawer } from './useEditPhysicianAmendmentNotesDrawer';
import type { EditPhysicianAmendmentNotesProps } from './editPhysicianAmendmentNotesDrawer.types';

const EditPhysicianAmendmentNotesDrawer = ({
  onClose,
  open,
  data,
  updateStudyData,
  drawerKey,
  ...params
}: EditPhysicianAmendmentNotesProps) => {
  const {
    state,
    setState,
    isAbortedStudy,
    handleChange,
    onEndSignature,
    handleClickSave,
    handleOnClose,
    onSave,
  } = useEditPhysicianAmendmentNotesDrawer({ onClose, open, data, updateStudyData, drawerKey });

  return (
    <Drawer
      {...params}
      open={open}
      rootClassName="edit-study-drawer-wrapper"
      title={t`Physician amendment notes`}
      aria-label={t`Physician amendment notes`}
      onClose={handleOnClose}
      destroyOnClose
      maskClosable={false}
    >
      {state.isInitiating ? (
        <div className="edit-study-details">
          <Skeleton className="initiating-skeleton" />
        </div>
      ) : (
        <>
          <ConfirmModal
            title={t`Apply changes`}
            width={600}
            question={
              <div>
                {getContent(true)}
                <div className="sign-modal">
                  <SignatureInput
                    signature={state.signature ?? ''}
                    onEndSignature={onEndSignature}
                    width={550}
                    height={300}
                  />
                </div>
              </div>
            }
            isOpen={state.isOpenSignModal}
            leftButtonName={t`Cancel`}
            rightButtonName={t`Sign & Save`}
            onClickLeftButton={() => setState({ isOpenSignModal: false })}
            onClickRightButton={onSave}
            disabledRightButton={!state.signature}
            isSaving={state.isSaving}
          />

          <div className="edit-study-details">
            <div className="content-container">
              {auth.isClinicPhysician() && (
                <div className="content">
                  <div className="left">
                    <div className="title">{t`Physician Amendment Notes`}</div>
                    <div className="warning">
                      <img src={inforOrangeIcon} alt="inforOrangeIcon" />
                      {t`To apply changes, you must provide your signature when editing physician amendment notes.`}
                    </div>
                  </div>

                  <div className="right">
                    <TextInput
                      className="has-default-height"
                      type="textarea"
                      name="physiciansNote"
                      value={state.physiciansNote}
                      isError={!!state.listError.physiciansNote}
                      errorMessage={state.listError.physiciansNote}
                      placeholder={t`Note`}
                      onChange={handleChange}
                      disabled={isAbortedStudy}
                    />

                    {isAbortedStudy && (
                      <div className="abort-input">
                        <img src={inforBlackIcon} alt="inforBlackIcon" />
                        {t`Cannot edit physician amendment notes of aborted study.`}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="edit-study-button-container">
              <CustomButton
                id="cancel-btn"
                type="outline"
                style="neutral"
                size="middle"
                onClick={handleOnClose}
              >
                {t`Cancel`}
              </CustomButton>

              <CustomButton
                id="save-btn"
                type="primary"
                style="default"
                size="middle"
                onClick={handleClickSave}
              >
                {t`Sign & Save`}
              </CustomButton>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default EditPhysicianAmendmentNotesDrawer;
